// Libs
import classNames from 'classnames/bind';
import {
  Button,
  Flex,
  IconCheckMarkSolid,
  IconWarningSolid,
  Link,
  ProgressBar,
  Spinner,
  Text,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  downloadDiscussionExportFile,
  getCurrentDiscussionExport,
  startDiscussionExport,
} from '~/thunks/discussionExport/discussionExportThunk';
import {
  DISCUSSION_EXPORT_MAX_LISTED_WARNINGS,
  DISCUSSION_EXPORT_NOTICE_MS,
  DISCUSSION_EXPORT_POLL_INTERVAL_MS,
  DISCUSSION_EXPORT_POLL_MAX_INTERVAL_MS,
  TIMEOUT_ALERT_MILLISECOND,
} from '~/utils/constants/common';
import {
  DiscussionExportPhaseEnum,
  DiscussionExportStatusEnum,
  DiscussionExportWarningSeverityEnum,
} from '~/utils/enum/common';
import { IDiscussionExportJob } from '~/utils/interface/discussionExport';
import { IToast } from '~/utils/interface/toast';
import { toastActions } from '~/thunks/toast/toastSlice';
import { generateGuid } from '~/utils/helper/common';
// Styles, images, icons
import styles from './DiscussionExport.module.scss';

const cx = classNames.bind(styles);

const NAMESPACE = 'admin/discussionExport';

// Business codes returned by the backend (DISCUSSION_EXPORT block + course not found).
const ERROR_MESSAGE_KEYS: Record<number, string> = {
  14001: 'discussion_export_error_not_found',
  14002: 'discussion_export_error_not_ready',
  14003: 'discussion_export_error_canvas_token',
  14004: 'discussion_export_error_canvas_permission',
  14005: 'discussion_export_error_canvas_unavailable',
  14006: 'discussion_export_error_pdf_renderer',
  14007: 'discussion_export_error_pdf_render',
  80001: 'discussion_export_error_course_not_found',
};

const getErrorCode = (error: unknown): number | undefined => {
  const value = error as { code?: unknown; response?: { data?: { code?: unknown } } } | undefined;
  const code = typeof value?.code === 'number' ? value.code : value?.response?.data?.code;
  return typeof code === 'number' ? code : undefined;
};

const getMissing = (job: IDiscussionExportJob) =>
  job.warnings.filter((warning) => warning.severity === DiscussionExportWarningSeverityEnum.MISSING);

/** Overall progress in %: listing 2-20, threads 20-90, PDF 95. */
const getProgressPercent = (job: IDiscussionExportJob): number => {
  const { phase, progress } = job;
  switch (phase) {
    case DiscussionExportPhaseEnum.LISTING:
      return progress.coursesTotal ? 2 + 18 * (progress.coursesDone / progress.coursesTotal) : 2;
    case DiscussionExportPhaseEnum.FETCHING:
      return progress.forumsTotal ? 20 + 70 * (progress.forumsDone / progress.forumsTotal) : 20;
    case DiscussionExportPhaseEnum.RENDERING:
    case DiscussionExportPhaseEnum.FINISHED:
      return 95;
    default:
      return 2;
  }
};

type Props = {
  /** Course chosen in the page's "Corso" field: the course export uses it. */
  selectedCourse?: { id: string; name?: string };
};

const DiscussionExport = (props: Props) => {
  //#region Destructuring Props
  const { selectedCourse } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation([NAMESPACE]);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Declare State
  const [job, setJob] = useState<IDiscussionExportJob | null>(null);
  const [isStarting, setIsStarting] = useState<boolean>(false);
  // Which button started the request, for its spinner until the job arrives.
  const [startingCourseId, setStartingCourseId] = useState<string | undefined>();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isInterrupted, setIsInterrupted] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const trackedJobIdRef = useRef<string | null>(null);
  //#endregion Declare State

  const isRunning = job?.status === DiscussionExportStatusEnum.RUNNING;
  // The spinner goes on the button of the export in progress: all courses or one course.
  const isBusyAll = (isStarting && !startingCourseId) || (isRunning && !job?.filters.courseId);
  const isBusyCourse = (isStarting && !!startingCourseId) || (isRunning && !!job?.filters.courseId);

  //#region Handle Function
  const showToast = useCallback(
    (type: IToast['type'], message: string, durationMs: number = TIMEOUT_ALERT_MILLISECOND) => {
      const messageId = generateGuid();
      dispatch(
        toastActions.addToast({ id: messageId, type, message, allowClose: durationMs > TIMEOUT_ALERT_MILLISECOND })
      );
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, durationMs);
    },
    [dispatch]
  );

  /** What a job exports: "Tutti i corsi" or "Corso: <nome>". */
  const getScopeLabel = (current: IDiscussionExportJob) =>
    current.filters.courseId
      ? t('discussion_export_scope_course', {
          ns: NAMESPACE,
          name: current.filters.courseName || current.filters.courseId,
        })
      : t('discussion_export_scope_all', { ns: NAMESPACE });

  const getErrorMessage = useCallback(
    (code?: number) => t(ERROR_MESSAGE_KEYS[code ?? 0] ?? 'discussion_export_error_generic', { ns: NAMESPACE }),
    [t]
  );

  // Applies a status read from the backend and keeps polling while the export runs.
  const applyJob = useCallback(
    (current: IDiscussionExportJob | null) => {
      const trackedJobId = trackedJobIdRef.current;
      if (trackedJobId && current?.id !== trackedJobId) {
        // The export we were following is gone: the server restarted (e.g. a deploy).
        trackedJobIdRef.current = null;
        setIsInterrupted(true);
      } else if (trackedJobId && current && current.status !== DiscussionExportStatusEnum.RUNNING) {
        trackedJobIdRef.current = null;
        if (current.status === DiscussionExportStatusEnum.DONE) {
          showToast('success', t('discussion_export_done_toast', { ns: NAMESPACE }));
        }
      }

      setJob(current);
      if (current?.status === DiscussionExportStatusEnum.RUNNING) {
        trackedJobIdRef.current = current.id;
        return true;
      }
      return false;
    },
    [showToast, t]
  );

  const schedulePoll = useCallback(
    (delay: number) => {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = setTimeout(async () => {
        try {
          const data = await dispatch(getCurrentDiscussionExport()).unwrap();
          if (!isMountedRef.current) return;
          if (applyJob(data?.job ?? null)) schedulePoll(DISCUSSION_EXPORT_POLL_INTERVAL_MS);
        } catch {
          // Backend unreachable (e.g. restarting): retry later, no toast.
          if (isMountedRef.current) schedulePoll(Math.min(delay * 2, DISCUSSION_EXPORT_POLL_MAX_INTERVAL_MS));
        }
      }, delay);
    },
    [dispatch, applyJob]
  );

  /** Without courseId: all courses. The backend replaces the previous PDF. */
  const handleStartExport = async (courseId?: string) => {
    if (isStarting || isRunning) return;
    setIsStarting(true);
    setStartingCourseId(courseId);
    setIsInterrupted(false);
    setShowDetails(false);
    try {
      const data = await dispatch(startDiscussionExport(courseId ? { courseId } : {})).unwrap();
      const started = data?.job ?? null;
      // One export at a time: the backend returns the running one if another admin started it.
      if (started && (started.filters.courseId ?? '') !== (courseId ?? '')) {
        const scope = getScopeLabel(started);
        showToast(
          'warning',
          started.requestedBy.name
            ? t('discussion_export_other_running', { ns: NAMESPACE, scope, name: started.requestedBy.name })
            : t('discussion_export_other_running_no_name', { ns: NAMESPACE, scope }),
          DISCUSSION_EXPORT_NOTICE_MS
        );
      }
      if (applyJob(started)) schedulePoll(DISCUSSION_EXPORT_POLL_INTERVAL_MS);
    } catch (error) {
      showToast('error', getErrorMessage(getErrorCode(error)));
    } finally {
      setIsStarting(false);
      setStartingCourseId(undefined);
    }
  };

  const handleStartCourseExport = () => {
    if (!selectedCourse?.id) {
      showToast('warning', t('discussion_export_select_course_first', { ns: NAMESPACE }));
      return;
    }
    handleStartExport(selectedCourse.id);
  };

  const handleDownload = async () => {
    if (!job?.fileName || isDownloading) return;
    setIsDownloading(true);
    try {
      await dispatch(downloadDiscussionExportFile({ jobId: job.id, fileName: job.fileName })).unwrap();
    } catch (error) {
      const code = getErrorCode(error);
      showToast('error', code ? getErrorMessage(code) : t('discussion_export_download_error', { ns: NAMESPACE }));
    } finally {
      setIsDownloading(false);
    }
  };
  //#endregion Handle Function

  //#region Implement Hook
  useEffect(() => {
    isMountedRef.current = true;
    // Resume the last export (it keeps running on the server while the page is closed).
    dispatch(getCurrentDiscussionExport())
      .unwrap()
      .then((data) => {
        if (isMountedRef.current && applyJob(data?.job ?? null)) {
          schedulePoll(DISCUSSION_EXPORT_POLL_INTERVAL_MS);
        }
      })
      .catch(() => {});

    return () => {
      isMountedRef.current = false;
      clearTimeout(pollTimerRef.current);
    };
  }, [dispatch, applyJob, schedulePoll]);
  //#endregion Implement Hook

  //#region Render
  // Everything stays on the header row: the page has a fixed height and the grading table below
  // only gets the height that is left, so an extra row would hide it on laptop screens.
  // Long course names are cut with an ellipsis; the full name is in the tooltip.
  const renderScope = (current: IDiscussionExportJob) => (
    <span className={cx('scope')} title={getScopeLabel(current)}>
      <Text size='small' weight='bold'>
        {getScopeLabel(current)}
      </Text>
    </span>
  );

  const renderRunning = (current: IDiscussionExportJob) => {
    const { progress } = current;
    const label =
      current.phase === DiscussionExportPhaseEnum.LISTING
        ? t('discussion_export_short_listing', {
            ns: NAMESPACE,
            done: progress.coursesDone,
            total: progress.coursesTotal,
          })
        : current.phase === DiscussionExportPhaseEnum.FETCHING
          ? t('discussion_export_short_fetching', {
              ns: NAMESPACE,
              done: progress.forumsDone,
              total: progress.forumsTotal,
            })
          : current.phase === DiscussionExportPhaseEnum.PREFLIGHT
            ? t('discussion_export_short_preflight', { ns: NAMESPACE })
            : t('discussion_export_short_rendering', { ns: NAMESPACE });
    const hint = [
      getScopeLabel(current),
      current.requestedBy.name && t('discussion_export_started_by', { ns: NAMESPACE, name: current.requestedBy.name }),
      t('discussion_export_running_hint', { ns: NAMESPACE }),
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <div className={cx('status')} title={hint}>
        {renderScope(current)}
        <Text size='small'>{label}</Text>
        <div className={cx('progress')}>
          <ProgressBar
            screenReaderLabel={t('discussion_export_progress_label', { ns: NAMESPACE })}
            valueNow={Math.round(getProgressPercent(current))}
            valueMax={100}
            size='x-small'
          />
        </div>
      </div>
    );
  };

  const renderDone = (current: IDiscussionExportJob) => (
    <>
      <div className={cx('status')}>
        {renderScope(current)}
        {current.complete ? (
          <>
            <IconCheckMarkSolid color='success' />
            <Text size='small' weight='bold' color='success'>
              {t('discussion_export_short_complete', { ns: NAMESPACE })}
            </Text>
          </>
        ) : (
          <>
            <IconWarningSolid color='warning' />
            <Text size='small' weight='bold' color='warning'>
              {t('discussion_export_short_incomplete', { ns: NAMESPACE, count: getMissing(current).length })}
            </Text>
            <Link as='button' isWithinText={false} onClick={() => setShowDetails((open) => !open)}>
              <Text size='small'>
                {t(showDetails ? 'discussion_export_details_hide' : 'discussion_export_details_show', {
                  ns: NAMESPACE,
                })}
              </Text>
            </Link>
          </>
        )}
      </div>

      <div className={cx('action')}>
        <Button color='primary' withBackground={false} type='button' disabled={isDownloading} onClick={handleDownload}>
          <Flex justifyItems='center' alignItems='center' gap='small'>
            <span>{t('discussion_export_download_pdf', { ns: NAMESPACE })}</span>
            {isDownloading && <Spinner size='x-small' />}
          </Flex>
        </Button>
      </div>
    </>
  );

  const renderFailed = (current: IDiscussionExportJob) => (
    <div className={cx('status')}>
      {renderScope(current)}
      <IconWarningSolid color='error' />
      <Text size='small' color='danger'>
        {t('discussion_export_failed', { ns: NAMESPACE })}: {getErrorMessage(current.error?.code)}
      </Text>
    </div>
  );

  const renderInterrupted = () => (
    <div className={cx('status')}>
      <IconWarningSolid color='warning' />
      <Text size='small' color='warning'>
        {t('discussion_export_interrupted', { ns: NAMESPACE })}
      </Text>
    </div>
  );

  // Missing items of an incomplete archive, only when asked for: a full-width row under the header.
  const renderDetails = (current: IDiscussionExportJob) => {
    const missing = getMissing(current);
    const listed = missing.slice(0, DISCUSSION_EXPORT_MAX_LISTED_WARNINGS);

    return (
      <div className={cx('panel')}>
        <Text size='small'>{t('discussion_export_done_incomplete', { ns: NAMESPACE, count: missing.length })}</Text>
        {listed.length > 0 && (
          <View as='div'>
            <Text size='small' weight='bold'>
              {t('discussion_export_missing_title', { ns: NAMESPACE })}
            </Text>
            <ul className={cx('warningList')}>
              {listed.map((warning, index) => (
                <li key={`${warning.forumId ?? warning.courseId}-${index}`}>
                  <Text size='small'>
                    {[warning.courseName, warning.forumTitle, warning.groupName].filter(Boolean).join(' · ')}
                    {' — '}
                    {warning.reason}
                  </Text>
                </li>
              ))}
            </ul>
            {missing.length > listed.length && (
              <Text size='small' color='secondary'>
                {t('discussion_export_more_warnings', { ns: NAMESPACE, count: missing.length - listed.length })}
              </Text>
            )}
          </View>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={cx('bar')}>
        {job?.status === DiscussionExportStatusEnum.RUNNING && renderRunning(job)}
        {job?.status === DiscussionExportStatusEnum.DONE && renderDone(job)}
        {job?.status === DiscussionExportStatusEnum.FAILED && renderFailed(job)}
        {isInterrupted && !job && renderInterrupted()}

        <div className={cx('action')}>
          <Button color='primary' type='button' disabled={isStarting || isRunning} onClick={() => handleStartExport()}>
            <Flex justifyItems='center' alignItems='center' gap='small'>
              <span>{t('discussion_export_button', { ns: NAMESPACE })}</span>
              {isBusyAll && <Spinner size='x-small' />}
            </Flex>
          </Button>
        </div>

        <div className={cx('action')}>
          <Button color='primary' type='button' disabled={isStarting || isRunning} onClick={handleStartCourseExport}>
            <Flex justifyItems='center' alignItems='center' gap='small'>
              <span>{t('discussion_export_course_button', { ns: NAMESPACE })}</span>
              {isBusyCourse && <Spinner size='x-small' />}
            </Flex>
          </Button>
        </div>
      </div>

      {showDetails && job?.status === DiscussionExportStatusEnum.DONE && !job.complete && renderDetails(job)}
    </>
  );
  //#endregion Render
};

export default DiscussionExport;
